import { useCallback, useMemo, useState } from "react"
import "../../styles/ui-document.css"
import type {
  ActionPayload,
  Block,
  RefreshEnvelope,
  V1Document,
} from "./types"
import { HeaderBlockView } from "./blocks/header"
import { DividerBlockView } from "./blocks/divider"
import { KeyValueBlockView } from "./blocks/key-value"
import { BadgeRowBlockView } from "./blocks/badge-row"
import { MetricGridBlockView } from "./blocks/metric-grid"
import { TextBlockView } from "./blocks/text"
import { TableBlockView } from "./blocks/table"
import { LinkRowBlockView } from "./blocks/link-row"
import { ActionButtonBlockView } from "./blocks/action-button"
import { ActionRowBlockView } from "./blocks/action-row"
import { EmptyStateBlockView } from "./blocks/empty-state"
import { CollapsibleBlockView } from "./blocks/collapsible"
import { CardListBlockView } from "./blocks/card-list"
import { UnsupportedBlock } from "./unsupported-block"

type OnActionResult = RefreshEnvelope | null

type Props = {
  document: V1Document
  // Caller fires the partner action through Valet's portal proxy and
  // returns the refresh envelope so the renderer can swap in-place
  // (`scope: partial`), refetch (`scope: full`), or do nothing
  // (`scope: none`). Returning null means the call failed (e.g. 4xx /
  // 5xx) — the renderer keeps the existing tree untouched and the hook
  // surfaces a sonner toast.
  onAction: (action: ActionPayload, refresh?: string[]) => Promise<OnActionResult>
  // Optional callback the parent uses to refetch the entire document
  // when a `scope: full` action response comes back. The renderer falls
  // back to a no-op if not provided, since the caller already busted
  // the cache server-side and the next tab open will see fresh data.
  onFullRefresh?: () => void
}

// Top-level UI Document renderer. Wraps every block in a try/catch so a
// single bad block degrades gracefully (renders <UnsupportedBlock />)
// rather than nuking the rest of the document.
//
// Container-query reflow lives entirely in `ui-document.css`; the
// wrapper sets `containerType: 'inline-size'` so child blocks can
// react to the panel width regardless of viewport size.
export function DocumentRenderer({ document, onAction, onFullRefresh }: Props) {
  // Per-id local override map for partial-refresh swaps. When an
  // `action` returns `scope: partial`, we replace the cached block here
  // instead of mutating the upstream `document` prop — that way
  // collapsibles, scroll positions, and other blocks' state survive.
  const [overrides, setOverrides] = useState<Map<string, Block>>(() => new Map())

  const blocks = Array.isArray(document.blocks) ? document.blocks : []

  const handleAction = useCallback(
    async (action: ActionPayload, refresh?: string[]) => {
      const envelope = await onAction(action, refresh ?? action.refresh)
      if (envelope === null) return null
      if (envelope.scope === "partial" && Array.isArray(envelope.blocks)) {
        setOverrides((prev) => {
          const next = new Map(prev)
          for (const entry of envelope.blocks) {
            if (entry && typeof entry.id === "string" && entry.block) {
              next.set(entry.id, entry.block)
            }
          }
          return next
        })
      } else if (envelope.scope === "full") {
        // Drop overrides — the parent will refetch and pass a fresh
        // document. If they don't, the natural 60s TTL will resolve it.
        setOverrides(new Map())
        onFullRefresh?.()
      }
      // scope: "none" → no visual change.
      return envelope
    },
    [onAction, onFullRefresh],
  )

  const renderBlock = useMemo(
    () => makeRenderBlock(handleAction, overrides),
    [handleAction, overrides],
  )

  return (
    <div className="valet-uidoc-root" style={{ containerType: "inline-size" }}>
      {(document.title || document.subtitle) && (
        <header className="mb-3 space-y-0.5">
          {document.title && (
            <h2 className="text-lg font-semibold text-app-text">{document.title}</h2>
          )}
          {document.subtitle && (
            <p className="text-sm text-app-muted">{document.subtitle}</p>
          )}
        </header>
      )}
      <div className="space-y-3">
        {blocks.map((b, idx) => (
          <div key={blockKey(b, idx)}>{renderBlock(b)}</div>
        ))}
      </div>
    </div>
  )
}

function blockKey(block: Block, idx: number): string {
  return block && typeof block === "object" && typeof block.id === "string"
    ? `id:${block.id}`
    : `idx:${idx}`
}

// Build the recursive block-render closure. Pulled out as a factory so
// `collapsible` blocks can call back into the same dispatcher without
// importing the top-level component (avoids a circular import).
function makeRenderBlock(
  handleAction: (action: ActionPayload, refresh?: string[]) => Promise<OnActionResult>,
  overrides: Map<string, Block>,
) {
  function render(block: Block): React.ReactNode {
    if (!block || typeof block !== "object") {
      return <UnsupportedBlock type="<malformed>" />
    }
    // Per-id partial-refresh override.
    const override = block.id ? overrides.get(block.id) : undefined
    const effective = override ?? block

    try {
      switch (effective.type) {
        case "header":        return <HeaderBlockView block={effective} />
        case "divider":       return <DividerBlockView />
        case "key_value":     return <KeyValueBlockView block={effective} />
        case "badge_row":     return <BadgeRowBlockView block={effective} />
        case "metric_grid":   return <MetricGridBlockView block={effective} />
        case "text":          return <TextBlockView block={effective} />
        case "table":         return <TableBlockView block={effective} onAction={handleAction} />
        case "link_row":      return <LinkRowBlockView block={effective} />
        case "action_button": return <ActionButtonBlockView block={effective} onAction={handleAction} />
        case "action_row":    return <ActionRowBlockView block={effective} onAction={handleAction} />
        case "empty_state":   return <EmptyStateBlockView block={effective} />
        case "collapsible":   return <CollapsibleBlockView block={effective} renderBlock={render} onAction={handleAction} />
        case "card_list":     return <CardListBlockView block={effective} />
        default: {
          // Type-system escape hatch for unknown / future block types.
          const t = (effective as { type?: string }).type ?? "<unknown>"
          return <UnsupportedBlock type={t} />
        }
      }
    } catch (err) {
      // Per-block try/catch — never let one bad block crash the rest of
      // the document. We don't surface a toast here (would spam if many
      // blocks fail simultaneously); the partner-side health panel
      // shows the schema_path of any block that failed validation.
      const t = (effective as { type?: string }).type ?? "<error>"
      if (typeof console !== "undefined" && console.warn) {
        console.warn("[ui-document] block render failed", { type: t, error: err })
      }
      return <UnsupportedBlock type={t} />
    }
  }
  return render
}
